import { i18n } from '@fs/zion-locale'
import * as Sentry from '@sentry/react'
import { v4 as uuid } from 'uuid'
import {
  getMostRecentFieldResource,
  isDateField,
  isPlaceField,
  getMostRecentFieldValue,
  getRecordTypesFromSurroundingRecords,
  getImage,
  getSlsTypesByKey,
  batchDeleteElementAndSubs,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
} from '@fs/zion-sls'

const SKIPPABLE_ERROR_MESSAGES = [
  'Network Error',
  'status code 404',
  'status code 401',
  'status code 403',
  'Request aborted',
]

function isSkippableError(errorMessage) {
  if (!errorMessage) return false
  return SKIPPABLE_ERROR_MESSAGES.some((msg) => errorMessage.includes(msg))
}

/**
 * Log error info to Splunk or Sentry or both if Sentry has been depleted
 * @param {*} error Error object or string
 * @param {*} image Could be an image ark or apid
 * @param {*} extraInfo Any extra info you want to log
 */
export function errorCollector(error, image, extraInfo = {}) {
  // Skip common network errors that aren't useful
  const errorMessage = error?.message || (typeof error === 'string' ? error : '')
  if (isSkippableError(errorMessage)) {
    return
  }

  console.error('ErrorCollector: ', error)
  if (typeof Sentry !== 'undefined' && Sentry) {
    Sentry.setContext('extraInfo', extraInfo)
    Sentry.setTag('codeId', extraInfo?.codeId || 0)
    Sentry.captureException(error)
    Sentry.setContext('') // Clear the context
    Sentry.setTag('codeId', 0)
  }
}

/**
 * Generate dispatches to delete relationships and their sub-elements
 * @param {Array} existingRelationships - Array of relationship elements to delete
 * @returns {Array} Array of dispatches for deleting relationships
 */
export function generateDeleteRelationshipDispatches(existingRelationships) {
  let deleteRelationshipDispatches = []
  existingRelationships.forEach((relationship) => {
    deleteRelationshipDispatches = deleteRelationshipDispatches.concat(
      batchDeleteElementAndSubs({ element: relationship })
    )
  })
  deleteRelationshipDispatches = deleteRelationshipDispatches.map((relDispatch) => ({
    ...relDispatch,
    value: {
      ...relDispatch.value,
    },
  }))
  return deleteRelationshipDispatches
}

/**
 * Generate dispatches to delete a person and their relationships
 * @param {string|Array} personIds - Single person ID or array of person IDs to delete
 * @param {string} partitionKey - Partition key for the person(s)
 * @param {Array} images - SLS images array to find the person(s) in
 * @returns {Array} Array of dispatches for deleting person(s) and their relationships
 */
export function generateDeletePersonDispatches(personIds, partitionKey, images) {
  // Handle single ID or array of IDs
  const ids = Array.isArray(personIds) ? personIds : [personIds]

  let deletePersonDispatches = []

  ids.forEach((id) => {
    // Add dispatch to remove the person
    deletePersonDispatches.push({
      type: 'REMOVE_ELEMENT',
      value: { id, elementType: 'PERSON', partitionKey },
    })

    // Get the person and delete their relationships
    const person = getNestedOrFloatingElementById(images, id)
    if (person) {
      const existingRelationships = getNestedSubelementsByType(person, 'RELATIONSHIP')
      const deleteRelationshipDispatches = generateDeleteRelationshipDispatches(existingRelationships)
      deletePersonDispatches = deletePersonDispatches.concat(deleteRelationshipDispatches)
    }
  })

  return deletePersonDispatches
}

export const getNextUuid = () => uuid()

export const getQueryValues = (queryString) => {
  // https://www.developerdrive.com/turning-the-querystring-into-a-json-object-using-javascript/
  // Go from '?lang=en&type=Birth' to {lang: 'en', type: 'Birth'}
  if (queryString) {
    const pairs = queryString.slice(1).split('&')
    const result = {}
    pairs.forEach((pair) => {
      pair = pair.split('=')
      result[pair[0]] = decodeURIComponent(pair[1] || '')
    })
    return result
  }
  return {}
}

export const getAssociatedFields = (person, fields) => {
  if (!person || !Array.isArray(person.fieldIds)) return []
  const associatedFields = person.fieldIds.map((fieldId) => {
    return fields?.find((field) => fieldId === field.id)
  })

  const sortMap = [
    { elementType: 'NAME_GN', value: 1 },
    { elementType: 'NAME_SURN', value: 2 },
    { elementType: 'SEX_CODE', value: 3 },
  ]

  associatedFields.sort((a, b) => {
    const foundA = sortMap.find((item) => item.elementType === a.elementType)
    const sortKeyA = foundA ? foundA.value : sortMap.length + 1

    const foundB = sortMap.find((item) => item.elementType === b.elementType)
    const sortKeyB = foundB ? foundB.value : sortMap.length + 1

    return sortKeyA - sortKeyB
  })
  return associatedFields.filter(Boolean)
}

export const getPersonFullName = (personId, state) => {
  if (!personId) return null
  const person = typeof personId === 'object' ? personId : state.persons.find((p) => p.id === personId)
  const associatedFields = getAssociatedFields(person, state?.fields)
  const foundGiven = associatedFields.find((field) => field.elementType === 'NAME_GN')
  const foundSurname = associatedFields.find((field) => field.elementType === 'NAME_SURN')

  const personFullName = `${foundGiven?.value ? `${foundGiven.value} ` : ''}${
    foundSurname?.value ? foundSurname.value : ''
  }`

  return personFullName || i18n.t('get-person-full-name.unknown', '<Unknown>')
}

/**
 * Async/await version of setTimeout
 * @param {Number} [ms] Milliseconds to wait before executing timeout
 * @returns {Promise} Resolves when the timeout completes
 * @async
 */
export function timeout(ms) {
  return new Promise((r) => {
    setTimeout(r, ms)
  })
}

/**
 * Yes, lodash has one but it throws an en exception with the data: { originalText: null, standard: null }
 * @param {Object} obj Object to check
 * @returns Boolean
 */
export const isEmpty = (obj) => {
  if (typeof obj !== 'object') return true
  let empty = true
  Object.keys(obj).forEach((prop) => {
    if (obj[prop] && obj[prop] !== '') empty = false
  })
  return empty
}

// Because the values get convoluted in lots of objects, undefined means value was unable to be found
export const getRawValue = (field) => {
  // We still want an empty string to be a valid value.
  const value = field?.value === '' ? '' : undefined || field?.value?.key || field?.value || field?.primaryText
  // Checks that the value exists and that a property in the value is something (checks for empty properties too)
  // The standards place, date fields send { originalText: null, standard: null } as a value if you enter nothing in the field
  if (value && typeof value === 'object') {
    return isEmpty(value) ? undefined : value
  }
  if (value || value === '') return value // Allow value to be an empty string if the patron wants to enter ''
  return undefined
}

const sortMap = [
  { elementType: 'EVENT', value: 1 },
  { elementType: 'DATE', value: 2 },
  { elementType: 'PLACE', value: 3 },
]

export const eventValuesByType = (a, b) => {
  const foundA = sortMap.find((item) => item.elementType === a.elementType)
  const foundB = sortMap.find((item) => item.elementType === b.elementType)

  const sortKeyA = foundA ? foundA.value : sortMap.length + 1
  const sortKeyB = foundB ? foundB.value : sortMap.length + 1

  return sortKeyA - sortKeyB
}

export const formatEventFields = (fields) =>
  fields
    .filter((f) => f.eventId)
    // This sorts the fields with events into a dictionary: {eventId1: [fields,...], eventId2: [fields,...],...}
    .reduce(
      (a, c) =>
        Object.keys(a).includes(c.eventId) ? { ...a, [c.eventId]: a[c.eventId].concat(c) } : { ...a, [c.eventId]: [c] },
      {}
    )

export const getPartitionKey = (imageArk) => imageArk.split('/').filter((x) => x.match(/3:\d:/g))[0]

export const hasName = (name) => i18n.t('select-person.list-item.text', '<Unknown>') !== name && name !== ''

export function replaceIndex(arr, i, replacement) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) throw new Error('You passed in an invalid array')
  if (i < 0) throw new Error('You tried to replace an array element with a negative index. Did findIndex fail?')
  if (i >= arr.length) throw new Error('You tried to replace an array element that is past the length of the array')
  return [...arr.slice(0, i), replacement, ...arr.slice(i + 1)]
}

export function removeIndex(arr, i) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) throw new Error('You passed in an invalid array')
  if (i >= arr.length) throw new Error('You tried to remove an array element that is past the length of the array')
  return [...arr.slice(0, i), ...arr.slice(i + 1)]
}

// History utils
// You should be able to put 0 for count if you don't want it to count
export function getMostRecentChanges(arr, property, id, count = 0) {
  const recents = []
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i][property]?.includes(id)) {
      recents.push(arr[i])
      if (--count === 0) break
    }
  }
  return recents.reverse()
}

/**
 *
 * @returns true/false - whether or not there is a context to navigate back to for RII and Explore Images
 */
export function getIndexExploreContextUrl() {
  const lastUrl = document.referrer
  const hasContext =
    lastUrl.includes('ark:/61903/1:1') ||
    lastUrl.includes('ark:/61903/1:2') ||
    lastUrl.includes('images/search-results') ||
    lastUrl.includes('images/beta/search-results')
  return hasContext ? lastUrl : null
}

/**
 * We need to set the form default values for place and date fields.  We were doing this in many places, thus the function
 * @param {Object} defaults - formDefaultValues
 * @param {Array} fields - array of field objects
 */
export function addPlaceAndDateFieldsToFormDefaults(defaults, fields) {
  if (Array.isArray(fields)) {
    fields.forEach((field) => {
      if (isPlaceField(field) || isDateField(field)) {
        const resource = getMostRecentFieldResource(field)
        defaults[field.id] = {
          originalText: getMostRecentFieldValue(field) || '',
          standard: resource
            ? {
                id: isPlaceField(field) ? resource.split('/')[resource.split('/').length - 1] : null,
                standardizedText: getMostRecentFieldValue(field) || '',
                formalText: isPlaceField(field) ? resource : null,
              }
            : null,
        }
      } else {
        defaults[field.id] = getMostRecentFieldValue(field) || '' // Set a string value instead of undefined so dirty checking will work properly.
      }
    })
  }
}

/**
 * Function for Autosuggest component sorting that prioritize exact matches over
 * alphabetically preceding partial matches
 *
 * @param {*} inputValue Input text by the user
 * @param {*} suggestionA "A" element for the array .sort() function
 * @param {*} suggestionB "B" element for the array .sort() function
 * @returns Compare function
 */
export function autosuggestPrioritySort(inputValue, suggestionA, suggestionB) {
  const normalizedInputText = inputValue.toLowerCase().trim()
  if (suggestionA.primaryText.toLowerCase().startsWith(normalizedInputText)) return -1
  if (suggestionB.primaryText.toLowerCase().startsWith(normalizedInputText)) return 1
  return suggestionA.primaryText - suggestionB.primaryText
}

/**
 * Function to retrieve an array of options for the autosuggest component for record types.
 * The result includes surrounding record types.
 *
 * @param {Array} zionSlsTypes - Array of types from the zionSlsState.
 * @param {Array} zionSlsImages - Array of images from the zionSlsState.
 * @param {Object} currentImageArk - The object for the current image ark from the zionSlsState.
 * @param {Function} t - Translation function from the useTranslation hook.
 * @param {Boolean} [filterOutTreeTypes=true] - Whether to remove tree types from the result.
 * @returns {Array} An array containing the options to pass to the zion's autosuggest component.
 */
export function getRecordTypesForAutosuggest(
  zionSlsTypes,
  zionSlsImages,
  currentImageArk,
  t,
  filterOutTreeTypes = true
) {
  // Getting types from the state
  const rawRecordTypes = getSlsTypesByKey(zionSlsTypes, 'records', 'recordType') || []

  // If filterOutTreeTypes is true, we filter out types that have the tree property set to true
  const stateRecordTypes = (filterOutTreeTypes ? rawRecordTypes.filter((r) => !r.tree) : rawRecordTypes)?.map((r) => ({
    key: r.recordType,
    primaryText: r.displayLabel,
    group: t('add-person.record-types', 'Record types'),
  }))

  const defaultRecordTypes = [
    { key: 'notImplemented', primaryText: t('add-person.types-not-implemented', 'Types Not Implemented') },
  ]

  const finalStateRecordTypes = stateRecordTypes.length ? stateRecordTypes : defaultRecordTypes
  const currentImage = getImage(zionSlsImages, currentImageArk)

  // Getting types from surrounding records
  const recordTypesInSurroundingImages =
    getRecordTypesFromSurroundingRecords(currentImage)
      ?.sort((a, b) => a.count - b.count)
      ?.slice(0, 3) // Gets the top 3 from the surrounding records
      .map((obj) => ({
        key: obj.type,
        primaryText: finalStateRecordTypes.find((r) => r.key === obj.type)?.primaryText,
        group: t('add-person.in-surrounding-records', 'In surrounding records'),
      }))
      .filter((obj) => obj.primaryText) || []

  // Filter out stateRecordTypes that have the same key as any in recordTypesInSurroundingImages
  const filteredStateRecordTypes = finalStateRecordTypes.filter(
    (recordType) => !recordTypesInSurroundingImages.some((surroundingType) => surroundingType.key === recordType.key)
  )

  return recordTypesInSurroundingImages.concat(filteredStateRecordTypes).reverse()
}

/**
 * Legal characters are alphanumeric, spaces, hyphens, single quotes, commas, and periods.
 */
export function filterIllegalReviewFieldCharacters(str) {
  return str.replace(/[^a-zA-Z0-9À-ž\s\-',.]/g, '')
}
